
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { toast } from 'sonner';
import { ViewClientDialog } from '@/components/sales/ViewClientDialog';
import {
  Search,
  Users,
  MoreVertical,
  Mail,
  Phone,
  Plus,
  Building,
  Globe,
  Star,
  StarOff,
  Eye,
  Pencil,
  Trash2,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { clientService, Client, ClientFormData, ClientStats } from '@/services/client.service';
import { useAuthStore } from '@/stores/authStore';




export default function Clients() {
  const { hasSubModulePermission } = useAuthStore();
  const [clients, setClients] = useState<Client[]>([]);
  const [stats, setStats] = useState<ClientStats>({
    activeClients: 0,
    totalDeals: 0,
    totalRevenue: 0,
    activeDeals: 0,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);

  const [showAddClient, setShowAddClient] = useState(false);
  const [showViewClient, setShowViewClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [showEditClient, setShowEditClient] = useState(false);
  const [editClient, setEditClient] = useState<ClientFormData>({
    name: '',
    industry: '',
    email: '',
    phone: '',
    status: 'Active',
    totalDeals: 0,
    totalRevenue: 0,
  });
  const [showDeleteClient, setShowDeleteClient] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [newClient, setNewClient] = useState<ClientFormData>({
    name: '',
    industry: '',
    email: '',
    phone: '',
    status: 'Active',
    totalDeals: 0,
    totalRevenue: 0,
  });
  const [newClientErrors, setNewClientErrors] = useState({
    name: '',
    industry: '',
    email: '',
    phone: '',
  });
  const [editClientErrors, setEditClientErrors] = useState({
    name: '',
    industry: '',
    email: '',
    phone: '',
  });

  useEffect(() => {
    loadClients();
    loadStats();
  }, [currentPage, pageSize, searchQuery]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await clientService.getClients({
        page: currentPage,
        limit: pageSize,
        search: searchQuery || undefined,
      });

      setClients(response.data.map(client => ({
        ...client,
        id: client._id || client.id,
      })));
      const total = response.total ?? response.pagination?.total ?? response.data.length;
      setTotalPages(Math.ceil(total / pageSize));
      setTotalClients(total);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await clientService.getClientStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleAddClient = async () => {
    const errors = {
      name: !newClient.name ? 'Client Name is required' : '',
      industry: !newClient.industry ? 'Industry is required' : '',
      email: !newClient.email ? 'Email is required' : '',
      phone: !newClient.phone ? 'Phone is required' : '',
    };
    setNewClientErrors(errors);

    if (!newClient.name || !newClient.email || !newClient.phone || !newClient.industry) {
      return;
    }

    try {
      setSubmitting(true);
      await clientService.createClient(newClient);

      setNewClient({
        name: '',
        industry: '',
        email: '',
        phone: '',
        status: 'Active',
        totalDeals: 0,
        totalRevenue: 0,
      });
      setNewClientErrors({ name: '', industry: '', email: '', phone: '' });
      setShowAddClient(false);

      await loadClients();
      await loadStats();

      toast.success("Client has been added successfully.");
    } catch (error: any) {
      console.error('Error adding client:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof ClientFormData, value: string | number) => {
    setNewClient(prev => ({ ...prev, [field]: value }));
    if (field === 'name' || field === 'industry' || field === 'email' || field === 'phone') {
      setNewClientErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setShowViewClient(true);
  };

  const openEditClient = (client: Client) => {
    setSelectedClient(client);
    setEditClient({
      name: client.name,
      industry: client.industry,
      contactPerson: client.contactPerson,
      email: client.email,
      phone: client.phone,
      status: client.status,
      totalDeals: client.totalDeals,
      totalRevenue: client.totalRevenue,
      isFavorite: client.isFavorite,
      notes: client.notes,
    });
    setShowEditClient(true);
  };

  const openDeleteClient = (client: Client) => {
    setSelectedClient(client);
    setShowDeleteClient(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedClient) return;

    try {
      setSubmitting(true);
      await clientService.deleteClient(selectedClient.id);

      setShowDeleteClient(false);
      setSelectedClient(null);

      await loadClients();
      await loadStats();

      toast.success(`${selectedClient.name} has been deleted successfully.`);
    } catch (error: any) {
      console.error('Error deleting client:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditInputChange = (field: keyof ClientFormData, value: string | number) => {
    setEditClient(prev => ({ ...prev, [field]: value }));
    if (field === 'name' || field === 'industry' || field === 'email' || field === 'phone') {
      setEditClientErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleUpdateClient = async () => {
    if (!selectedClient) return;
    const errors = {
      name: !editClient.name ? 'Client Name is required' : '',
      industry: !editClient.industry ? 'Industry is required' : '',
      email: !editClient.email ? 'Email is required' : '',
      phone: !editClient.phone ? 'Phone is required' : '',
    };
    setEditClientErrors(errors);

    if (!editClient.name || !editClient.email || !editClient.phone || !editClient.industry) {
      return;
    }

    try {
      setSubmitting(true);
      await clientService.updateClient(selectedClient.id, editClient);

      setShowEditClient(false);

      await loadClients();
      await loadStats();

      toast.success('Client has been updated successfully.');
    } catch (error: any) {
      console.error('Error updating client:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Removed favorite toggle interaction; show status-based icon in list instead

  const startIndex = clients.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const endIndex = clients.length === 0 ? 0 : Math.min(startIndex + pageSize, totalClients || clients.length);

  const handleRefresh = async () => {
    setCurrentPage(1);
    setSearchQuery("");
    await loadClients();
    await loadStats();
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(parseInt(newPageSize));
    setCurrentPage(1);
  };

  const debouncedSearch = (() => {
    let timeoutId: NodeJS.Timeout;
    return (query: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setSearchQuery(query);
        setCurrentPage(1);
      }, 500);
    };
  })();

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };


  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Clients</h1>
          <p className="text-muted-foreground">
            Manage your client relationships and information
          </p>
        </div>

        {hasSubModulePermission('sales_clients', 'client', 'create') && (
          <Button onClick={() => setShowAddClient(true)} className="rounded-xl">
            <Plus className="h-4 w-4 mr-2" />
            Add Client
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Clients</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>


            <div className="text-2xl font-bold">{stats.activeClients}</div>
            <p className="text-xs text-muted-foreground">
              Active clients
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Deals</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>

            <div className="text-2xl font-bold">{stats.totalDeals}</div>
            <p className="text-xs text-muted-foreground">
              Across all clients
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>

            <div className="text-2xl font-bold">${stats.totalRevenue.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              From all clients
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Deals</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>

            <div className="text-2xl font-bold">{stats.activeDeals}</div>
            <p className="text-xs text-muted-foreground">
              In progress
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Client List</CardTitle>
              <CardDescription>View and manage your clients</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />


                <Input
                  placeholder="Search clients..."
                  className="pl-8"
                  onChange={handleSearchChange}
                />
              </div>
              <div className="flex items-center gap-2">
                <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                  <SelectTrigger className="w-[120px] rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5 rows</SelectItem>
                    <SelectItem value="10">10 rows</SelectItem>
                    <SelectItem value="25">25 rows</SelectItem>
                    <SelectItem value="50">50 rows</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleRefresh}
                  className="rounded-xl"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table className="min-w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Industry</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Deals</TableHead>
                  <TableHead>Revenue</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading clients...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No clients found
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {client.status === 'Active' ? (
                            <Star className="h-4 w-4 text-yellow-400" />
                          ) : (
                            <StarOff className="h-4 w-4 text-muted-foreground" />
                          )}
                          {client.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{client.email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{client.phone}</span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{client.industry}</TableCell>
                      <TableCell>
                        <Badge
                          variant={client.status === 'Active' ? 'default' : 'secondary'}
                        >
                          {client.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{client.totalDeals}</TableCell>
                      <TableCell>${client.totalRevenue.toLocaleString()}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">

                            <DropdownMenuItem onClick={() => handleViewClient(client)}>
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>

                            {hasSubModulePermission('sales_clients', 'client', 'edit') && (
                              <DropdownMenuItem onClick={() => openEditClient(client)}>
                                <Pencil className="h-4 w-4 mr-2" />
                                Edit Client
                              </DropdownMenuItem>
                            )}
                            {hasSubModulePermission('sales_clients', 'client', 'delete') && (
                              <DropdownMenuItem onClick={() => openDeleteClient(client)} className="text-red-600 focus:text-red-600">
                                <Trash2 className="h-4 w-4 mr-2 text-red-600" />
                                Delete Client
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>


        {/* Pagination */}
        <div className="flex items-center justify-between px-6 pb-4">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, totalClients)} of {totalClients} entries
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded-xl"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>

            <div className="flex items-center gap-1">
              {(() => {
                const pageButtons: JSX.Element[] = [];

                if (totalPages > 3) {
                  let startPage = Math.max(1, currentPage - 1);
                  let endPage = Math.min(totalPages, currentPage + 1);

                  if (startPage === 1) {
                    endPage = Math.min(totalPages, 3);
                  }
                  if (endPage === totalPages) {
                    startPage = Math.max(1, totalPages - 2);
                  }

                  if (startPage > 1) {
                    pageButtons.push(
                      <Button
                        key={1}
                        variant={1 === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(1)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        1
                      </Button>
                    );
                    if (startPage > 2) {
                      pageButtons.push(
                        <span key="ellipsis-start" className="px-2 text-muted-foreground">
                          ..
                        </span>
                      );
                    }
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pageButtons.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    );
                  }

                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pageButtons.push(
                        <span key="ellipsis-end" className="px-2 text-muted-foreground">
                          ..
                        </span>
                      );
                    }
                    pageButtons.push(
                      <Button
                        key={totalPages}
                        variant={totalPages === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(totalPages)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {totalPages}
                      </Button>
                    );
                  }
                } else {
                  for (let i = 1; i <= totalPages; i++) {
                    pageButtons.push(
                      <Button
                        key={i}
                        variant={i === currentPage ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(i)}
                        className="rounded-xl w-8 h-8 p-0"
                      >
                        {i}
                      </Button>
                    );
                  }
                }

                return pageButtons;
              })()}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="rounded-xl"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </Card>


      {/* Add Client Modal */}
      <Dialog open={showAddClient} onOpenChange={(open) => { setShowAddClient(open); if (!open) setNewClientErrors({ name: '', industry: '', email: '', phone: '' }); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Plus className="h-5 w-5 text-blue-600" />
              Add New Client
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-[5px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client-name">Client Name *</Label>
                  <Input
                    id="client-name"
                    value={newClient.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Enter client name"
                    className="rounded-xl"
                  />
                  {newClientErrors.name && <div className="text-red-600 text-xs mt-1">{newClientErrors.name}</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-industry">Industry *</Label>
                  <Select value={newClient.industry} onValueChange={(value) => handleInputChange('industry', value)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Technology">Technology</SelectItem>
                      <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="Software">Software</SelectItem>
                      <SelectItem value="Research">Research</SelectItem>
                      <SelectItem value="Healthcare">Healthcare</SelectItem>
                      <SelectItem value="Finance">Finance</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Retail">Retail</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {newClientErrors.industry && <div className="text-red-600 text-xs mt-1">{newClientErrors.industry}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client-email">Email *</Label>
                  <Input
                    id="client-email"
                    type="email"
                    value={newClient.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    className="rounded-xl"
                  />
                  {newClientErrors.email && <div className="text-red-600 text-xs mt-1">{newClientErrors.email}</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-phone">Phone *</Label>
                  <Input
                    id="client-phone"
                    value={newClient.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                    className="rounded-xl"
                  />
                  {newClientErrors.phone && <div className="text-red-600 text-xs mt-1">{newClientErrors.phone}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="client-status">Status</Label>
                  <Select value={newClient.status} onValueChange={(value: 'Active' | 'Inactive' | 'Pending') => handleInputChange('status', value)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-deals">Deals</Label>
                  <Input
                    id="client-deals"
                    type="number"
                    min="0"
                    value={newClient.totalDeals}
                    onChange={(e) => handleInputChange('totalDeals', parseInt(e.target.value) || 0)}
                    placeholder="Enter number of deals"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="client-revenue">Revenue ($)</Label>
                  <Input
                    id="client-revenue"
                    type="number"
                    min="0"
                    step="0.01"
                    value={newClient.totalRevenue}
                    onChange={(e) => handleInputChange('totalRevenue', parseFloat(e.target.value) || 0)}
                    placeholder="Enter revenue amount"
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowAddClient(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddClient}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Plus className="h-4 w-4 mr-2" />
              )}
              Add Client
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Client Modal */}
      <Dialog open={showEditClient} onOpenChange={(open) => { setShowEditClient(open); if (!open) setEditClientErrors({ name: '', industry: '', email: '', phone: '' }); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-gray-900 flex items-center gap-2">
              <Pencil className="h-5 w-5 text-blue-600" />
              Edit Client
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-6 p-[5px]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-client-name">Client Name *</Label>
                  <Input
                    id="edit-client-name"
                    value={editClient.name}
                    onChange={(e) => handleEditInputChange('name', e.target.value)}
                    placeholder="Enter client name"
                    className="rounded-xl"
                  />
                  {editClientErrors.name && <div className="text-red-600 text-xs mt-1">{editClientErrors.name}</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-client-industry">Industry *</Label>
                  <Select value={editClient.industry} onValueChange={(value) => handleEditInputChange('industry', value)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Select industry" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Technology">Technology</SelectItem>
                      <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                      <SelectItem value="Software">Software</SelectItem>
                      <SelectItem value="Research">Research</SelectItem>
                      <SelectItem value="Healthcare">Healthcare</SelectItem>
                      <SelectItem value="Finance">Finance</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Retail">Retail</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {editClientErrors.industry && <div className="text-red-600 text-xs mt-1">{editClientErrors.industry}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-client-email">Email *</Label>
                  <Input
                    id="edit-client-email"
                    type="email"
                    value={editClient.email}
                    onChange={(e) => handleEditInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                    className="rounded-xl"
                  />
                  {editClientErrors.email && <div className="text-red-600 text-xs mt-1">{editClientErrors.email}</div>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-client-phone">Phone *</Label>
                  <Input
                    id="edit-client-phone"
                    value={editClient.phone}
                    onChange={(e) => handleEditInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                    className="rounded-xl"
                  />
                  {editClientErrors.phone && <div className="text-red-600 text-xs mt-1">{editClientErrors.phone}</div>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-client-status">Status</Label>
                  <Select value={editClient.status} onValueChange={(value: 'Active' | 'Inactive' | 'Pending') => handleEditInputChange('status', value)}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Active">Active</SelectItem>
                      <SelectItem value="Inactive">Inactive</SelectItem>
                      <SelectItem value="Pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-client-deals">Deals</Label>
                  <Input
                    id="edit-client-deals"
                    type="number"
                    min="0"
                    value={editClient.totalDeals}
                    onChange={(e) => handleEditInputChange('totalDeals', parseInt(e.target.value) || 0)}
                    placeholder="Enter number of deals"
                    className="rounded-xl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-client-revenue">Revenue ($)</Label>
                  <Input
                    id="edit-client-revenue"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editClient.totalRevenue}
                    onChange={(e) => handleEditInputChange('totalRevenue', parseFloat(e.target.value) || 0)}
                    placeholder="Enter revenue amount"
                    className="rounded-xl"
                  />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3">
            <Button
              variant="outline"
              onClick={() => setShowEditClient(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateClient}
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirm */}
      <AlertDialog open={showDeleteClient} onOpenChange={setShowDeleteClient}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete
              {selectedClient ? ` ${selectedClient.name}` : ' this client'} from your list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* View Client Dialog */}
      <ViewClientDialog
        client={selectedClient}
        open={showViewClient}
        onOpenChange={setShowViewClient}
      />
    </div>
  );

} 